/**
 * Three.js相关API
 * 包含Three.js交互式演示代码生成功能
 */

import Logger from "../utils/logger";
import { API_PATHS } from "./config";
import { convertImageToBase64, getCurrentUser, getCustomClientSlug, requestModelByModeConfig } from "./utils";

/**
 * 从AI响应中提取HTML代码
 * @param {string} response AI的完整响应
 * @returns {string} 提取的HTML代码
 */
const extractHTMLCode = async (response) => {
  try {
    Logger.debug("开始提取HTML代码", { responseLength: response.length });

    // 记录原始响应的前500个字符用于调试
    Logger.debug("AI响应前500字符:", response.substring(0, 500));

    let htmlCode = null;
    let extractMethod = "";

    // 1. 尝试匹配标准HTML代码块（更灵活的正则）
    const htmlCodeRegex = /```\s*html?\s*([\s\S]*?)\s*```/gi;
    let match = htmlCodeRegex.exec(response);

    if (match && match[1]) {
      htmlCode = match[1].trim();
      extractMethod = "html代码块";
      Logger.debug("通过html代码块找到代码", { codeLength: htmlCode.length });
    }

    // 2. 如果没找到，尝试匹配任何代码块（可能没有语言标识符）
    if (!htmlCode) {
      const anyCodeRegex = /```\s*([\s\S]*?)\s*```/g;
      const matches = [...response.matchAll(anyCodeRegex)];

      // 在所有代码块中找包含HTML标签的
      for (const codeMatch of matches) {
        const code = codeMatch[1].trim();
        if (
          code.includes("<html") ||
          code.includes("<!DOCTYPE") ||
          code.includes("<head") ||
          code.includes("<body")
        ) {
          htmlCode = code;
          extractMethod = "通用代码块";
          Logger.debug("通过通用代码块找到HTML代码", {
            codeLength: htmlCode.length,
          });
          break;
        }
      }
    }

    // 3. 如果还没找到，尝试查找完整的HTML文档（不在代码块中）
    if (!htmlCode) {
      const docRegex = /<!DOCTYPE\s+html[\s\S]*?<\/html>/gi;
      match = docRegex.exec(response);

      if (match) {
        htmlCode = match[0];
        extractMethod = "完整HTML文档";
        Logger.debug("找到完整HTML文档", { codeLength: htmlCode.length });
      }
    }

    // 4. 尝试查找任何HTML标签结构
    if (!htmlCode) {
      const htmlTagRegex = /<html[\s\S]*?<\/html>/gi;
      match = htmlTagRegex.exec(response);

      if (match) {
        htmlCode = match[0];
        extractMethod = "HTML标签结构";
        Logger.debug("找到HTML标签结构", { codeLength: htmlCode.length });
      }
    }

    // 5. 最后的尝试：查找包含Three.js关键词的长文本块
    if (!htmlCode) {
      if (
        response.includes("THREE.") &&
        response.includes("<script") &&
        response.length > 1000
      ) {
        // 可能AI直接返回了HTML代码而没有用代码块包装
        const lines = response.split("\n");
        let htmlStart = -1;
        let htmlEnd = -1;

        // 查找包含HTML结构的部分
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim().toLowerCase();
          if (line.includes("<!doctype") || line.includes("<html")) {
            htmlStart = i;
            break;
          }
        }

        for (let i = lines.length - 1; i >= 0; i--) {
          const line = lines[i].trim().toLowerCase();
          if (line.includes("</html>")) {
            htmlEnd = i;
            break;
          }
        }

        if (htmlStart >= 0 && htmlEnd > htmlStart) {
          htmlCode = lines.slice(htmlStart, htmlEnd + 1).join("\n");
          extractMethod = "文本块检测";
          Logger.debug("通过文本块检测找到HTML代码", {
            htmlStart,
            htmlEnd,
            codeLength: htmlCode.length,
          });
        }
      }
    }

    // 如果找到了HTML代码，进行标准化处理
    if (htmlCode) {
      Logger.info(`成功提取HTML代码 (方法: ${extractMethod})`, {
        originalLength: htmlCode.length,
        containsThreeJS: htmlCode.includes("THREE."),
        containsScript: htmlCode.includes("<script"),
        containsCanvas: htmlCode.includes("<canvas"),
      });

      // 确保HTML代码完整
      if (!htmlCode.includes("<!DOCTYPE")) {
        htmlCode = "<!DOCTYPE html>\n" + htmlCode;
      }

      if (!htmlCode.includes("<html")) {
        htmlCode = htmlCode.replace(
          "<!DOCTYPE html>",
          '<!DOCTYPE html>\n<html lang="zh-CN">'
        );
      }

      if (!htmlCode.includes("</html>")) {
        htmlCode += "\n</html>";
      }

      Logger.debug("HTML代码标准化完成", { finalLength: htmlCode.length });
      return htmlCode;
    }

    // 如果所有尝试都失败了，记录详细的调试信息
    Logger.warn("未找到HTML代码，详细信息:", {
      responseLength: response.length,
      hasCodeBlocks: response.includes("```"),
      hasHtmlTags: response.includes("<html"),
      hasScriptTags: response.includes("<script"),
      hasThreeJS: response.includes("THREE."),
      firstCodeBlock: response.includes("```")
        ? response.split("```")[1]?.substring(0, 100)
        : "none",
    });

    return null;
  } catch (error) {
    Logger.error("提取HTML代码失败", error);
    return null;
  }
};

/**
 * 生成Three.js交互式解题演示代码
 * @param {string} prompt 用户输入的问题描述
 * @param {Function} onChunkReceived 接收流式响应的回调函数
 * @param {Function} onComplete 完成时的回调函数
 * @param {Function} onError 错误处理回调函数
 * @param {File|File[]} imageFiles 可选的图片文件（最多3张）
 * @param {boolean} freeMode 是否使用免费模型与免费渠道
 */
export const streamThreeJSCode = async (
  prompt,
  onChunkReceived,
  onComplete,
  onError,
  imageFiles,
  freeMode = false
) => {
  const imageFilesArr = Array.isArray(imageFiles) ? imageFiles : (imageFiles ? [imageFiles] : []);
  Logger.info("开始流式生成Three.js代码", {
    prompt,
    imageCount: imageFilesArr.length,
  });

  try {
    // 构建请求体
    const requestBody = {
      mode: "threejs",
      prompt: prompt,
      stream: false, // 改为非流式响应
      free_mode: freeMode,
    };
    const slug = getCustomClientSlug();
    if (slug) requestBody.custom_client_slug = slug;

    // 如果有图片，将所有图片转为 base64 数组
    if (imageFilesArr.length > 0) {
      const base64Images = await Promise.all(imageFilesArr.map(convertImageToBase64));
      requestBody.images = base64Images;
    }

    // 添加用户ID
    const currentUser = getCurrentUser();
    requestBody.userId = currentUser?.id || 0;

    Logger.debug("发送Three.js代码生成非流式API请求", requestBody);

    const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

    Logger.info("Three.js代码流式生成完成", {
      responseLength: fullResponse.length,
    });

    const htmlCode = await extractHTMLCode(fullResponse);
    if (!htmlCode || htmlCode.trim() === "") {
      Logger.error("从流式响应中提取的HTML代码为空");
      onError("生成的HTML代码为空，请重试");
      return;
    }

    Logger.info("HTML代码提取成功", { codeLength: htmlCode.length });

    if (onComplete) {
      onComplete({
        success: true,
        code: htmlCode,
        fullResponse: fullResponse,
        chargeSession,
      });
      Logger.info("onComplete回调调用成功");
    }
  } catch (error) {
    const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : `生成Three.js代码失败: ${error?.message || error}`;
    onError(msg);
  }
};

export default {
  streamThreeJSCode,
};
